import { AtLeast } from '../helpers'
import {
  type Block,
  type DocumentBlock,
  type MediaBlock,
  type ListBlock,
  type RootBlock,
  type ComponentBlock,
  type InstanceBlock,
  type BlockType,
  type RichTextBlock,
  type ReferenceBlock,
  type CommentBlock,
  type SelectBlock,
  type BlockChange,
  type NumberBlock,
  type TextBlock,
  type DateBlock,
  type ColorBlock,
  type CodeSnippetBlock,
  type OgImageBlock,
  type BooleanBlock,
  type VariantSetBlock,
  type WorkflowBlock,
  type EventBlock,
  type IconBlock,
  isVirtualizedInstanceChildTargetBR,
  AiAutofillAction,
  AiAutofillVariantAction,
  AgentBlock,
} from './block'
import {
  BlockRelationshipForHash,
  areBRsCompatible,
  type BlockRelationship,
} from './block-relationship'
import type { Branch, Playground } from './branch'
import type { Commit } from './commit'

export function isCommit(
  branchOrCommit: Branch | Commit
): branchOrCommit is Commit {
  return (branchOrCommit as Commit).message !== undefined
}

export function isBranch(
  branchOrCommit: Branch | Commit
): branchOrCommit is Branch {
  return (branchOrCommit as Branch).headCommitId !== undefined
}

export function isPlayground(
  branchOrCommit: Branch | Commit
): branchOrCommit is Playground {
  return (
    isBranch(branchOrCommit) &&
    branchOrCommit.playgroundId !== undefined &&
    !!branchOrCommit.playgroundId
  )
}

export function isPlaygroundOrPlaygroundCommit(
  branchOrCommit: Branch | Commit
): branchOrCommit is Branch {
  return 'playgroundId' in branchOrCommit
}

export function isRootBlock(block: AtLeast<Block, 'type'>): block is RootBlock {
  return block.type === 'root'
}

export function isDocumentBlock(
  block: AtLeast<Block, 'type'>
): block is DocumentBlock {
  return block.type === 'document'
}

export function isListBlock(block: AtLeast<Block, 'type'>): block is ListBlock {
  return block.type === 'list'
}

export function isEventBlock(
  block: AtLeast<Block, 'type'>
): block is EventBlock {
  return block.type === 'event'
}

export function isInstanceBlock(
  block: AtLeast<Block, 'type'>
): block is InstanceBlock {
  return block.type === 'instance'
}

export function isComponentBlock(
  block: AtLeast<Block, 'type'>
): block is ComponentBlock {
  return block.type === 'component'
}

export function isDocumentLikeBlock(
  block: AtLeast<Block, 'type'>
): block is ComponentBlock | InstanceBlock | DocumentBlock {
  return (
    block.type === 'document' ||
    block.type === 'component' ||
    block.type === 'instance'
  )
}

export function isRichTextBlock(
  block: AtLeast<Block, 'type'>
): block is RichTextBlock {
  return block.type === 'rich-text'
}

export function isSelectBlock(
  block: AtLeast<Block, 'type'>
): block is SelectBlock {
  return block.type === 'select'
}

export function isCommentBlock(
  block: AtLeast<Block, 'type'>
): block is CommentBlock {
  return block.type === 'comment'
}

export function isOgImageBlock(
  block: AtLeast<Block, 'type'>
): block is OgImageBlock {
  return block.type === 'og-image'
}

export function isWorkflowBlock(
  block: AtLeast<Block, 'type'>
): block is WorkflowBlock {
  return block.type === 'workflow'
}

export type WorkflowTriggerBlockChangeCompatible = Exclude<
  Block,
  | {
      type: 'workflow' | 'comment' | 'variant-set' | 'event' | 'og-image'
    }
  | Exclude<BlockWithChildren, RichTextBlock>
>
export type WorkflowActionBlockChangeCompatible = Exclude<
  Block,
  | {
      type: 'workflow' | 'comment' | 'variant-set' | 'event' | 'og-image'
    }
  | Exclude<BlockWithChildren, RichTextBlock | ReferenceBlock>
>
export function isWorkflowTriggerBlockChangeCompatible(
  block: AtLeast<Block, 'type'>
): block is WorkflowTriggerBlockChangeCompatible {
  return (
    !isCommentBlock(block) &&
    !isVariantSetBlock(block) &&
    !isWorkflowBlock(block) &&
    !isEventBlock(block) &&
    !isOgImageBlock(block) &&
    (!isBlockWithChildren(block) || isRichTextBlock(block))
  )
}
export function isWorkflowActionBlockChangeCompatible(
  block: AtLeast<Block, 'type'>
): block is WorkflowActionBlockChangeCompatible {
  return (
    !isCommentBlock(block) &&
    !isVariantSetBlock(block) &&
    !isWorkflowBlock(block) &&
    !isEventBlock(block) &&
    !isOgImageBlock(block) &&
    (!isBlockWithChildren(block) ||
      isRichTextBlock(block) ||
      isReferenceBlock(block))
  )
}

export const isAutofillAction = (
  action: WorkflowBlock['value']['actions'][number]
): action is AiAutofillAction | AiAutofillVariantAction =>
  action.type === 'ai-autofill' || action.type === 'ai-autofill-variant'

export function isRuntimeWorkflow(
  workflowBlock: WorkflowBlock
): workflowBlock is WorkflowBlock & {
  value: { trigger: { type: 'layout-block' } }
} {
  return workflowBlock.value.trigger?.type === 'layout-block'
}

export function isCommitWorkflow(
  workflowBlock: WorkflowBlock
): workflowBlock is WorkflowBlock & {
  value: { trigger: { type: 'commit' | 'list-block' } }
} {
  return (
    workflowBlock.value.trigger?.type === 'commit' ||
    workflowBlock.value.trigger?.type === 'list-block'
  )
}
export function isEventWorkflow(
  workflowBlock: WorkflowBlock
): workflowBlock is WorkflowBlock & {
  value: { trigger: { type: 'event-block' } }
} {
  return workflowBlock.value.trigger?.type === 'event-block'
}

export function isListWorkflow(
  workflowBlock: WorkflowBlock
): workflowBlock is WorkflowBlock & {
  value: { trigger: { type: 'list-block' } }
} {
  return workflowBlock.value.trigger?.type === 'list-block'
}

export function isVariantSetBlock(
  block: AtLeast<Block, 'type'>
): block is VariantSetBlock {
  return block.type === 'variant-set'
}

export function isColorBlock(
  block: AtLeast<Block, 'type'>
): block is ColorBlock {
  return block.type === 'color'
}

export function isRootOrDocumentBlock(
  block: AtLeast<Block, 'type'>
): block is RootBlock | DocumentBlock {
  return isRootBlock(block) || isDocumentBlock(block)
}

export function isReferenceBlock(
  block: AtLeast<Block, 'type'>
): block is ReferenceBlock {
  return block.type === 'reference'
}

export function isReferenceableBlock(
  block: AtLeast<Block, 'type'>
): block is InstanceBlock | ComponentBlock {
  return isInstanceBlock(block) || isComponentBlock(block)
}

export function isTextBlock(block: AtLeast<Block, 'type'>): block is TextBlock {
  return block.type === 'text'
}

export type HidableBlock = ComponentBlock | InstanceBlock | DocumentBlock

export const isHidableBlock = (
  block: AtLeast<Block, 'type'>
): block is HidableBlock => {
  return (
    isComponentBlock(block) || isInstanceBlock(block) || isDocumentBlock(block)
  )
}

export type BlockWithoutChildren =
  | NumberBlock
  | TextBlock
  | MediaBlock
  | DateBlock
  | ColorBlock
  | CodeSnippetBlock
  | OgImageBlock
  | SelectBlock
  | CommentBlock
  | BooleanBlock
  | ReferenceBlock

export type BlockWithChildren =
  | RootBlock
  | DocumentBlock
  | ListBlock
  | InstanceBlock
  | ComponentBlock
  | RichTextBlock
  | ReferenceBlock

export function isBlockWithChildren(
  block: AtLeast<Block, 'type'>
): block is BlockWithChildren {
  return (
    isRootBlock(block) ||
    isDocumentBlock(block) ||
    isListBlock(block) ||
    isInstanceBlock(block) ||
    isRichTextBlock(block) ||
    isComponentBlock(block) ||
    isReferenceBlock(block)
  )
}

export const isTypeForBlockWithChildren = (type: BlockType) =>
  [
    'root',
    'document',
    'list',
    'instance',
    'component',
    'rich-text',
    'reference',
  ].includes(type)

export const isMediaBlock = (
  block: AtLeast<Block, 'type'>
): block is MediaBlock => block.type === 'media'

export const isAudioMedia = (
  value: MediaBlock['value']
): value is Extract<
  MediaBlock['value'],
  { type: 'audio'; status: 'uploaded' }
> => {
  return value.status === 'uploaded' && value.type === 'audio'
}

export function isChangeBlock(block: unknown): block is BlockChange {
  return typeof block === 'object' && block !== null && 'status' in block
}

/** Validates if the block can be edited without needing to worry about API changes.
 * This is the case for instances child of collections, unions or rich-texts */
export function isNonCrucialBlock(
  block: AtLeast<Block, 'type'>,
  parent: AtLeast<Block, 'type'> | null | undefined
): block is InstanceBlock | ComponentBlock {
  if (!parent) return false
  // is list template and is not hidden – we block the template component entirely to avoid confusion
  // const childShownComponent =
  //   isComponentBlock(block) && !block.hidden && isListBlock(parent)
  // if (childShownComponent) return true

  return (
    isInstanceBlock(block) &&
    ['reference', 'list', 'rich-text'].includes(parent.type)
  )
}

export type BlockWithOverrides =
  | TextBlock
  | NumberBlock
  | ColorBlock
  | DateBlock
  | SelectBlock
  | BooleanBlock
  | ReferenceBlock
  | MediaBlock
  | RichTextBlock
  | CodeSnippetBlock
  | IconBlock
  | OgImageBlock

export const isBlockWithVariantOverrides = (
  block: AtLeast<Block, 'type'>
): block is BlockWithOverrides => {
  return [
    'text',
    'number',
    'color',
    'date',
    'select',
    'boolean',
    'reference',
    'media',
    'icon',
    'rich-text',
    'code-snippet',
    'og-image',
  ].includes(block.type)
}

export const hasVariantOverrides = (
  block: Block
): block is BlockWithOverrides => {
  return isBlockWithVariantOverrides(block) && Boolean(block.variantOverrides)
}

export type BlockWithVariants =
  | ComponentBlock
  | DocumentBlock
  | BlockWithOverrides

export const isBlockWithVariants = (
  block: Block
): block is BlockWithVariants => {
  return (
    isBlockWithVariantOverrides(block) ||
    ['document', 'component'].includes(block.type)
  )
}

export const isBlockWithTitleOverrides = (
  block: Block,
  parent: Block | null | undefined
): block is InstanceBlock => {
  return isInstanceBlock(block) && isNonCrucialBlock(block, parent)
}

export const isBlockRelationship = (
  blockOrBr: Block | BlockRelationship
): blockOrBr is BlockRelationship => {
  return 'order' in blockOrBr
}

type LightRoot = AtLeast<
  RootBlock,
  'id' | 'createdAt' | 'isCommitted' | 'branchId' | 'branchLineage'
>

export const isBRMoreImportant = (
  br1: BlockRelationship,
  br2: BlockRelationship,
  rootBlock: LightRoot,
  opts?: {
    omitDiscardedAtComparison?: boolean
    blockIdHint?: string
  }
): boolean => {
  if (br1.blockId === rootBlock.id && br2.blockId !== rootBlock.id) return true
  if (br1.blockId !== rootBlock.id && br2.blockId === rootBlock.id) return false

  if (
    !isBRInRoot(br1, rootBlock, {
      omitValidToComparison: true,
      omitDiscardedAtComparison: opts?.omitDiscardedAtComparison,
    })
  ) {
    return false
  }
  if (
    !isBRInRoot(br2, rootBlock, {
      omitValidToComparison: true,
      omitDiscardedAtComparison: opts?.omitDiscardedAtComparison,
    })
  ) {
    return true
  }

  const br1LineageIdx =
    rootBlock.branchId === br1.branchId
      ? 0
      : (rootBlock.branchLineage?.findIndex((l) => {
          return (
            l.branchId === br1.branchId &&
            l.lastSyncedAt >= br1.validFrom &&
            (!br1.validTo || l.lastSyncedAt <= br1.validTo)
          )
        }) ?? Infinity) + 1

  const br2LineageIdx =
    rootBlock.branchId === br2.branchId
      ? 0
      : (rootBlock.branchLineage?.findIndex((l) => {
          return (
            l.branchId === br2.branchId &&
            l.lastSyncedAt >= br2.validFrom &&
            (!br2.validTo || l.lastSyncedAt <= br2.validTo)
          )
        }) ?? Infinity) + 1

  if (br1LineageIdx > br2LineageIdx) return false
  if (br1LineageIdx < br2LineageIdx) return true

  if (br1.placeholderSince && !br2.placeholderSince) return true
  if (!br1.placeholderSince && br2.placeholderSince) return false

  if (
    !isBRInRoot(br1, rootBlock, {
      omitDiscardedAtComparison: opts?.omitDiscardedAtComparison,
    })
  ) {
    return false
  }
  if (
    !isBRInRoot(br2, rootBlock, {
      omitDiscardedAtComparison: opts?.omitDiscardedAtComparison,
    })
  ) {
    return true
  }

  if (
    isVirtualizedInstanceChildTargetBR(br1) &&
    !isVirtualizedInstanceChildTargetBR(br2)
  ) {
    return false
  }
  if (
    !isVirtualizedInstanceChildTargetBR(br1) &&
    isVirtualizedInstanceChildTargetBR(br2)
  ) {
    return true
  }

  if (!br1.isCommitted && br2.isCommitted) return true
  if (br1.isCommitted && !br2.isCommitted) return false
  if (br1.validFrom > br2.validFrom) return true

  if (opts?.blockIdHint) {
    if (br1.blockId === opts.blockIdHint && br2.blockId !== opts.blockIdHint) {
      return true
    }
    if (br2.blockId === opts.blockIdHint && br1.blockId !== opts.blockIdHint) {
      return false
    }
  }

  // just use the id to make this deterministic at least
  return br1.id > br2.id
}

export const isBRInRoot = (
  br: BlockRelationship,
  root: LightRoot,
  opts?: {
    omitValidToComparison?: boolean
    omitDiscardedAtComparison?: boolean
  }
) => {
  if (!opts?.omitDiscardedAtComparison && br.discardedAt) return false
  if (root.isCommitted && !br.isCommitted) return false
  if (!root.isInlineSuggestion && br.isInlineSuggestion) return false

  if (br.sourceBlockId === root.sourceBlockId) {
    // for roots, we require strict blockId match
    if (br.blockId !== root.id) return false
  }

  const passesRootTemporalCheck =
    br.validFrom <= root.createdAt &&
    (opts?.omitValidToComparison || !br.validTo || br.validTo >= root.createdAt)

  if (!passesRootTemporalCheck) return false

  if (root.branchId === br.branchId) {
    return true
  } else {
    if (
      !br.isCommitted &&
      (!root.isInlineSuggestion || br.isInlineSuggestion)
    ) {
      // can't reuse un-committed blocks in different branches
      // nor inline suggestion BRs from different branches
      return false
    }
    const inLineageIdx =
      root.branchLineage?.findIndex((l) => {
        return (
          l.branchId === br.branchId &&
          l.lastSyncedAt >= br.validFrom &&
          (!br.validTo || l.lastSyncedAt <= br.validTo)
        )
      }) ?? -1

    if (inLineageIdx === -1) {
      return false
    }
    return true
  }
}

export const areBRsEqual = (
  br1: BlockRelationshipForHash,
  br2: BlockRelationshipForHash
) => {
  return areBRsCompatible(
    { ...br1, blockId: 'same' },
    { ...br2, blockId: 'same' }
  )
}

export const isAgentBlock = (block: Block): block is AgentBlock => {
  return block.type === 'agent'
}
